import type { AlgoliaProductHit } from '../../../types/algolia';

import Head from 'next/head';
import { useSortBy } from 'react-instantsearch';

import { SortingOptionsSuffix } from '../../../utilities/constants/algolia';
import { SCHEMA_CONTEXT, schemaType } from '../../../utilities/constants/schemaTypes';
import { useAppContext } from '../../../utilities/context/static/AppContext';
import { useProductListContext } from '../../../utilities/context/static/ProductListContext';
import { localisedData } from '../../../utilities/helpers';
import { useAlgoliaSortingOptions } from '../../../utilities/hooks/search/useAlgoliaSortingOptions';

const ITEM_LIST_ORDER = {
  unordered: 'https://schema.org/ItemListUnordered',
  ascending: 'https://schema.org/ItemListOrderAscending',
  descending: 'https://schema.org/ItemListOrderDescending',
} as const;

const SORT_TO_ITEM_LIST_ORDER: Record<string, string> = {
  [SortingOptionsSuffix.RELEVANCE]: ITEM_LIST_ORDER.unordered,
  [SortingOptionsSuffix.PRICE_ASC]: ITEM_LIST_ORDER.ascending,
  [SortingOptionsSuffix.RC_NEW]: ITEM_LIST_ORDER.descending,
  [SortingOptionsSuffix.RC_SALE]: ITEM_LIST_ORDER.descending,
  [SortingOptionsSuffix.RC_BESTSELLERS]: ITEM_LIST_ORDER.descending,
  [SortingOptionsSuffix.PRICE_DESC]: ITEM_LIST_ORDER.descending,
};

const getItemListOrder = (sortSuffix: string | undefined): string =>
  (sortSuffix && SORT_TO_ITEM_LIST_ORDER[sortSuffix]) || ITEM_LIST_ORDER.unordered;

interface PLPItemListSchemaProps {
  hits: AlgoliaProductHit[];
}

export const PLPItemListSchema = ({ hits }: PLPItemListSchemaProps) => {
  const { productionHost, lang, locale } = useAppContext();
  const { categoryDetails } = useProductListContext();
  const { sortingOptions } = useAlgoliaSortingOptions();
  const { currentRefinement } = useSortBy({ items: sortingOptions });

  if (!hits.length || !categoryDetails?.categoryName) {
    return null;
  }

  const currentSortOption = sortingOptions.find(option => option.value === currentRefinement);
  const itemListOrder = getItemListOrder(currentSortOption?.suffix);

  const itemListSchema = {
    '@context': SCHEMA_CONTEXT,
    '@type': schemaType.itemList,
    name: categoryDetails.categoryName,
    itemListOrder,
    numberOfItems: hits.length,
    itemListElement: hits.map((hit, index) => ({
      '@type': schemaType.listItem,
      position: index + 1,
      url: `${productionHost}/${locale}${hit.url}`,
      name: localisedData(hit, hit.name, lang),
    })),
  };

  return (
    <Head>
      <script
        id="plp-itemlist-structured-data"
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
      />
    </Head>
  );
};
